import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import queryString from 'query-string'
import { IconFileTypePdf } from '@tabler/icons-react'

import { Breadcrumbs } from '@/components/forms/Breadcrumbs/Breadcrumbs'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { Progress } from '@/components/progress/Progress'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { getCourses, getUserTrackings } from '@/reducers/baltoAcademy/baltoAcademy.actions'

import { categorizeCourses, formatHMS, formatShortDate } from './BaltoAcademy.helpers'
import { buildUserTrackingsPdf, getUserPdfFileName } from './academyUserPdf.helpers'
import './BaltoAcademyReportingPage.scss'

const emptyTotals = { viewed: 0, completed: 0, averageProgress: 0 }

const BaltoAcademyUserPage = () => {
  const dispatch = useDispatch()
  // identifier + org are query params, not path segments — an email in the
  // path breaks the SPA history fallback on refresh.
  const location = useLocation()
  const { identifier: username, org } = queryString.parse(location.search)
  const { courses } = useSelector((state) => state.baltoAcademy)
  const { parent_id: parentOrgId } = useSelector((state) => state.currentOrganization)

  const [trackings, setTrackings] = useState([])
  const [totals, setTotals] = useState(emptyTotals)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    dispatch(getCourses(org))
  }, [org])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    dispatch(getUserTrackings(username))
      .then((data) => {
        if (!cancelled) {
          setTrackings(data.trackings)
          setTotals(data.totals)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [username])

  // Hidden `order` sort key mirrors the Reporting/Courses page ordering.
  const orderByCourseId = useMemo(() => {
    const ordered = Object.values(categorizeCourses(courses, parentOrgId)).flat()
    return new Map(ordered.map((course, position) => [course.id, position]))
  }, [courses, parentOrgId])

  const rows = useMemo(
    () =>
      trackings.map((tracking) => ({
        index: tracking.id,
        order: { value: orderByCourseId.get(tracking.courseId) ?? orderByCourseId.size },
        course: {
          value: tracking.course?.title ?? '—',
          as: <span>{tracking.course?.title ?? '—'}</span>,
        },
        progressPercent: {
          value: tracking.progressPercent ?? 0,
          as: (
            <div className="academy-reporting__progress">
              <Progress
                className="academy-reporting__progress-bar"
                value={tracking.progressPercent}
              />
              <span className="academy-reporting__progress-pct">
                {Math.round(tracking.progressPercent || 0)}%
              </span>
            </div>
          ),
        },
        totalTime: {
          value: tracking.totalTime ?? 0,
          as: <span className="academy-reporting__mono">{formatHMS(tracking.totalTime)}</span>,
        },
        commenced: {
          value: tracking.commenced,
          as: <span>{formatShortDate(tracking.commenced)}</span>,
        },
        completed: {
          value: tracking.completed,
          as: <span>{formatShortDate(tracking.completed)}</span>,
        },
      })),
    [trackings, orderByCourseId]
  )

  const columns = [
    { accessor: 'course', label: 'Course', isSearchable: true },
    { accessor: 'progressPercent', label: 'Progress' },
    { accessor: 'totalTime', label: 'Total Time' },
    { accessor: 'commenced', label: 'Started' },
    { accessor: 'completed', label: 'Completed' },
  ]

  const reportingLink = org
    ? `/balto-academy/reporting?${queryString.stringify({ org })}`
    : '/balto-academy/reporting'

  const handleExportPdf = () => {
    const orderedTrackings = [...trackings].sort(
      (first, second) =>
        (orderByCourseId.get(first.courseId) ?? orderByCourseId.size) -
        (orderByCourseId.get(second.courseId) ?? orderByCourseId.size)
    )
    const doc = buildUserTrackingsPdf({ identifier: username, trackings: orderedTrackings })
    doc.save(getUserPdfFileName(username))
  }

  return (
    <div data-testid="academy-user-page">
      <Breadcrumbs
        backLinks={[
          { label: 'Reporting', link: reportingLink, dataTestId: 'breadcrumb-reporting' },
        ]}
        currentLink={username}
      />

      <div className="academy-reporting__kpis">
        <div className="academy-reporting__kpi">
          <div className="academy-reporting__kpi-label">Courses</div>
          <div className="academy-reporting__kpi-value">{trackings.length.toLocaleString()}</div>
          <div className="academy-reporting__kpi-sub">assigned</div>
        </div>
        <div className="academy-reporting__kpi">
          <div className="academy-reporting__kpi-label">Viewed</div>
          <div className="academy-reporting__kpi-value">{totals.viewed.toLocaleString()}</div>
          <div className="academy-reporting__kpi-sub">started at least once</div>
        </div>
        <div className="academy-reporting__kpi">
          <div className="academy-reporting__kpi-label">Completed</div>
          <div className="academy-reporting__kpi-value academy-reporting__kpi-value--passed">
            {totals.completed.toLocaleString()}
          </div>
          <div className="academy-reporting__kpi-sub">finished courses</div>
        </div>
        <div className="academy-reporting__kpi">
          <div className="academy-reporting__kpi-label">Avg progress</div>
          <div className="academy-reporting__kpi-value">{totals.averageProgress}%</div>
          <div className="academy-reporting__kpi-sub">across viewed courses</div>
        </div>
      </div>

      <div className="flex-end mb">
        <Button
          onClick={handleExportPdf}
          disabled={loading || isEmpty(trackings)}
          icon={<IconFileTypePdf />}
          data-testid="export-user-pdf"
        >
          Export PDF
        </Button>
      </div>

      <Card className="no-padding">
        <AdvancedTable
          loading={loading}
          rows={rows}
          columns={columns}
          defaultOrderBy="order"
          defaultOrder="asc"
          pagination
          rowsPerPage={15}
          index="index"
          itemName="courses"
          noDataText="No academy activity for this user."
        />
      </Card>
    </div>
  )
}

export default BaltoAcademyUserPage
